import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  TrendingDown,
  Hash,
  Calendar,
  Search,
  Plus,
  Camera,
  Loader2,
  Paperclip,
  Sparkles,
  Receipt,
  AlertCircle,
  Trash2,
} from "lucide-react";
import {
  expensesApi,
  chartOfAccountsApi,
  bankAccountsApi,
  mattersApi,
  taxSettingsApi,
} from "../../services/api";
import { TaxConfig, getEffectiveRate, getTaxLabel, DEFAULT_TAX_CONFIG } from "../../utils/taxCalculation";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Expense {
  _id: string;
  date: string;
  description: string;
  vendorName: string | null;
  vendorContactId: string | null;
  accountId: string | { _id: string; code: string; name: string };
  amountCents: number;
  taxAmountCents: number;
  totalCents: number;
  paymentMethod: "cash" | "debit" | "credit_card" | "eft" | "cheque";
  bankAccountId: string | null;
  matterId: string | { _id: string; title: string } | null;
  clientId: string | null;
  receiptDocumentId: string | null;
  receiptOcrData: any;
  category: string | null;
  notes: string | null;
  createdAt: string;
}

interface ChartAccount {
  _id: string;
  code: string;
  name: string;
  type: string;
}

interface BankAccount {
  _id: string;
  name: string;
  kind: string;
  institution: string | null;
  accountLast4: string | null;
}

interface Matter {
  _id: string;
  title: string;
  matterNumber?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number): string {
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return cents < 0 ? `-${formatted}` : formatted;
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "debit", label: "Debit" },
  { value: "credit_card", label: "Credit Card" },
  { value: "eft", label: "EFT" },
  { value: "cheque", label: "Cheque" },
];

const METHOD_BADGE: Record<string, { bg: string; text: string }> = {
  cash: { bg: "bg-emerald-50", text: "text-emerald-700" },
  debit: { bg: "bg-blue-50", text: "text-blue-700" },
  credit_card: { bg: "bg-purple-50", text: "text-purple-700" },
  eft: { bg: "bg-indigo-50", text: "text-indigo-700" },
  cheque: { bg: "bg-slate-100", text: "text-slate-700" },
};

const METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  debit: "Debit",
  credit_card: "Credit Card",
  eft: "EFT",
  cheque: "Cheque",
};

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface ExpenseForm {
  date: string;
  description: string;
  vendorName: string;
  accountId: string;
  amount: string; // dollars string
  taxAmount: string; // dollars string
  paymentMethod: string;
  bankAccountId: string;
  matterId: string;
  notes: string;
}

const emptyForm: ExpenseForm = {
  date: todayISO(),
  description: "",
  vendorName: "",
  accountId: "",
  amount: "",
  taxAmount: "",
  paymentMethod: "",
  bankAccountId: "",
  matterId: "",
  notes: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ExpensesTab: React.FC = () => {
  // Data
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterAccountId, setFilterAccountId] = useState<string | null>(null);
  const [filterMethod, setFilterMethod] = useState<string | null>(null);

  // Lookup data
  const [expenseAccounts, setExpenseAccounts] = useState<ChartAccount[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [matters, setMatters] = useState<Matter[]>([]);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [form, setForm] = useState<ExpenseForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [ocrBanner, setOcrBanner] = useState(false);

  // OCR
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hover state for buttons
  const [hoverAdd, setHoverAdd] = useState(false);
  const [hoverSave, setHoverSave] = useState(false);

  // Tax config
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  useEffect(() => {
    taxSettingsApi.get().then((res) => {
      const d = res.data?.data || res.data;
      if (d?.taxMode) {
        setTaxConfig({ taxMode: d.taxMode, hstRate: d.hstRate ?? 13, gstRate: d.gstRate ?? 5, pstRate: d.pstRate ?? 0 });
      }
    }).catch(() => {});
  }, []);

  // -----------------------------------------------------------------------
  // Load data
  // -----------------------------------------------------------------------

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: 500 };
      if (filterAccountId) params.accountId = filterAccountId;
      if (filterMethod) params.paymentMethod = filterMethod;
      const res = await expensesApi.list(params);
      setExpenses(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load expenses:", err);
    } finally {
      setLoading(false);
    }
  }, [filterAccountId, filterMethod]);

  const loadLookups = useCallback(async () => {
    try {
      const [accountsRes, bankRes, mattersRes] = await Promise.all([
        chartOfAccountsApi.list(),
        bankAccountsApi.list({ status: "open" }),
        mattersApi.list({ limit: 500 }),
      ]);
      setExpenseAccounts(accountsRes.data?.data?.expense || []);
      setBankAccounts(bankRes.data?.data || bankRes.data || []);
      setMatters(mattersRes.data?.data || mattersRes.data || []);
    } catch (err) {
      console.error("Failed to load lookup data:", err);
    }
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  // -----------------------------------------------------------------------
  // Computed
  // -----------------------------------------------------------------------

  const filtered = search
    ? expenses.filter((exp) => {
        const q = search.toLowerCase();
        return (
          (exp.description || "").toLowerCase().includes(q) ||
          (exp.vendorName || "").toLowerCase().includes(q)
        );
      })
    : expenses;

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Summary calculations
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const thisMonthExpenses = expenses.filter((exp) => {
    const d = new Date(exp.date);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  });

  const ytdExpenses = expenses.filter((exp) => {
    const d = new Date(exp.date);
    return d.getFullYear() === currentYear;
  });

  const thisMonthTotal = thisMonthExpenses.reduce(
    (sum, exp) => sum + exp.totalCents,
    0
  );
  const thisMonthCount = thisMonthExpenses.length;
  const ytdTotal = ytdExpenses.reduce((sum, exp) => sum + exp.totalCents, 0);

  // Select options
  const expenseAccountOptions = expenseAccounts.map((a) => ({
    value: a._id,
    label: `${a.code} - ${a.name}`,
  }));

  const bankAccountOptions = bankAccounts.map((a) => ({
    value: a._id,
    label: `${a.name}${a.accountLast4 ? ` (...${a.accountLast4})` : ""}`,
  }));

  const matterOptions = matters.map((m) => ({
    value: m._id,
    label: m.title,
  }));

  // -----------------------------------------------------------------------
  // Form helpers
  // -----------------------------------------------------------------------

  const computeTotal = (amount: string, tax: string): string => {
    const a = parseFloat(amount) || 0;
    const t = parseFloat(tax) || 0;
    return (a + t).toFixed(2);
  };

  const autoCalcTax = (amount: string): string => {
    const a = parseFloat(amount) || 0;
    const rate = getEffectiveRate(taxConfig) / 100;
    return (a * rate).toFixed(2);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFormError("");
    setOcrBanner(false);
    setEditingExpense(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (expense: Expense) => {
    setEditingExpense(expense);
    setOcrBanner(false);
    setFormError("");

    const accountIdStr =
      typeof expense.accountId === "object" && expense.accountId
        ? expense.accountId._id
        : (expense.accountId as string) || "";

    const matterIdStr =
      typeof expense.matterId === "object" && expense.matterId
        ? expense.matterId._id
        : (expense.matterId as string) || "";

    setForm({
      date: expense.date ? new Date(expense.date).toISOString().slice(0, 10) : todayISO(),
      description: expense.description || "",
      vendorName: expense.vendorName || "",
      accountId: accountIdStr,
      amount: (expense.amountCents / 100).toFixed(2),
      taxAmount: (expense.taxAmountCents / 100).toFixed(2),
      paymentMethod: expense.paymentMethod || "",
      bankAccountId: expense.bankAccountId || "",
      matterId: matterIdStr,
      notes: expense.notes || "",
    });
    setModalOpen(true);
  };

  // -----------------------------------------------------------------------
  // Save
  // -----------------------------------------------------------------------

  const handleSave = async () => {
    setFormError("");

    if (!form.description.trim()) {
      setFormError("Description is required.");
      return;
    }
    if (!form.accountId) {
      setFormError("Expense account is required.");
      return;
    }
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      setFormError("Amount must be greater than zero.");
      return;
    }
    if (!form.paymentMethod) {
      setFormError("Payment method is required.");
      return;
    }

    const amountCents = Math.round(amount * 100);
    const taxAmountCents = Math.round((parseFloat(form.taxAmount) || 0) * 100);

    const totalCents = amountCents + taxAmountCents;

    const payload: Record<string, any> = {
      date: form.date || todayISO(),
      description: form.description.trim(),
      vendorName: form.vendorName.trim() || null,
      accountId: form.accountId,
      amountCents,
      taxAmountCents,
      totalCents,
      paymentMethod: form.paymentMethod,
      bankAccountId: form.bankAccountId || null,
      matterId: form.matterId || null,
      notes: form.notes.trim() || null,
    };

    setSubmitting(true);
    try {
      if (editingExpense) {
        await expensesApi.update(editingExpense._id, payload);
      } else {
        await expensesApi.create(payload);
      }
      setModalOpen(false);
      resetForm();
      loadExpenses();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || "Failed to save expense.";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Delete
  // -----------------------------------------------------------------------

  const handleDelete = async () => {
    if (!editingExpense) return;
    if (
      !window.confirm(
        "Delete this expense? The journal entry will be reversed."
      )
    )
      return;

    setSubmitting(true);
    try {
      await expensesApi.remove(editingExpense._id);
      setModalOpen(false);
      resetForm();
      loadExpenses();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || "Failed to delete expense.";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // OCR flow
  // -----------------------------------------------------------------------

  const handleScanClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    e.target.value = "";

    setScanning(true);
    try {
      const formData = new FormData();
      formData.append("receipt", file);

      const res = await expensesApi.scanReceipt(formData);
      const ocr = res.data;

      // Pre-fill form with OCR data
      resetForm();
      setForm({
        date: ocr.date
          ? new Date(ocr.date).toISOString().slice(0, 10)
          : todayISO(),
        description: "",
        vendorName: ocr.vendorName || "",
        accountId: "",
        amount: ocr.subtotalCents
          ? (ocr.subtotalCents / 100).toFixed(2)
          : "",
        taxAmount: ocr.taxCents
          ? (ocr.taxCents / 100).toFixed(2)
          : "",
        paymentMethod: ocr.paymentMethod || "",
        bankAccountId: "",
        matterId: "",
        notes: "",
      });
      setOcrBanner(true);
      setModalOpen(true);
    } catch (err) {
      console.error("Receipt OCR failed:", err);
      // Open empty create modal on failure
      resetForm();
      setFormError("Receipt scan failed. Please enter details manually.");
      setModalOpen(true);
    } finally {
      setScanning(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const getAccountDisplay = (
    accountId: Expense["accountId"]
  ): string => {
    if (typeof accountId === "object" && accountId) {
      return `${accountId.code} ${accountId.name}`;
    }
    // If just a string ID, look up from our loaded accounts
    const found = expenseAccounts.find((a) => a._id === accountId);
    if (found) return `${found.code} ${found.name}`;
    return "-";
  };

  const getMatterTitle = (
    matterId: Expense["matterId"]
  ): string => {
    if (typeof matterId === "object" && matterId) {
      return matterId.title;
    }
    if (typeof matterId === "string" && matterId) {
      const found = matters.find((m) => m._id === matterId);
      if (found) return found.title;
    }
    return "-";
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div>
      {/* Hidden file input for OCR */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          {
            label: "This Month",
            value: formatCents(thisMonthTotal),
            Icon: TrendingDown,
            color: "text-red-700",
            bg: "bg-red-50",
          },
          {
            label: "Expense Count",
            value: thisMonthCount.toString(),
            Icon: Hash,
            color: "text-blue-700",
            bg: "bg-blue-50",
          },
          {
            label: "Year to Date",
            value: formatCents(ytdTotal),
            Icon: Calendar,
            color: "text-amber-700",
            bg: "bg-amber-50",
          },
        ].map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${card.bg}`}>
                <card.Icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">
                  {card.label}
                </p>
                <p className={`text-lg font-bold ${card.color}`}>
                  {card.value}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by description or vendor..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Expense Account filter */}
          <div className="w-full lg:w-52">
            <SearchableSelect
              options={expenseAccountOptions}
              value={filterAccountId}
              onChange={(val) => setFilterAccountId(val)}
              placeholder="All Accounts"
              clearable
            />
          </div>

          {/* Payment Method filter */}
          <div className="w-full lg:w-44">
            <SearchableSelect
              options={PAYMENT_METHODS}
              value={filterMethod}
              onChange={(val) => setFilterMethod(val)}
              placeholder="All Methods"
              clearable
            />
          </div>

          {/* Scan Receipt */}
          <button
            onClick={handleScanClick}
            disabled={scanning}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {scanning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Scanning receipt...
              </>
            ) : (
              <>
                <Camera className="h-4 w-4" />
                Scan Receipt
              </>
            )}
          </button>

          {/* Add Expense */}
          <button
            onClick={openCreateModal}
            onMouseEnter={() => setHoverAdd(true)}
            onMouseLeave={() => setHoverAdd(false)}
            style={{
              backgroundColor: hoverAdd
                ? "var(--color-primary-hover)"
                : "var(--color-primary)",
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">
            {search || filterAccountId || filterMethod
              ? "No matching expenses"
              : "No expenses recorded"}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {search || filterAccountId || filterMethod
              ? "Try adjusting your filters"
              : "Click \"Add Expense\" to record your first expense"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand text-white text-left">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold">Vendor</th>
                  <th className="px-4 py-3 font-semibold">Account</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Amount
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">Tax</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Total
                  </th>
                  <th className="px-4 py-3 font-semibold">Method</th>
                  <th className="px-4 py-3 font-semibold">Matter</th>
                  <th className="px-4 py-3 font-semibold text-center">
                    Receipt
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((exp, i) => {
                  const badge =
                    METHOD_BADGE[exp.paymentMethod] || METHOD_BADGE.cash;
                  return (
                    <motion.tr
                      key={exp._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.015 }}
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => openEditModal(exp)}
                    >
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-xs">
                        {formatDate(exp.date)}
                      </td>
                      <td className="px-4 py-3 text-slate-800 font-medium max-w-[200px] truncate">
                        {exp.description}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate">
                        {exp.vendorName || "-"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs max-w-[160px] truncate">
                        {getAccountDisplay(exp.accountId)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">
                        {formatCents(exp.amountCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500">
                        {formatCents(exp.taxAmountCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">
                        {formatCents(exp.totalCents)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.bg} ${badge.text}`}
                        >
                          {METHOD_LABELS[exp.paymentMethod] ||
                            exp.paymentMethod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs max-w-[140px] truncate">
                        {getMatterTitle(exp.matterId)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Paperclip
                          className={`h-4 w-4 mx-auto ${
                            exp.receiptDocumentId
                              ? "text-emerald-600"
                              : "text-slate-300"
                          }`}
                        />
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                  <td colSpan={4} className="px-4 py-3 text-xs text-slate-600">
                    Total ({sorted.length} expense
                    {sorted.length !== 1 ? "s" : ""})
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">
                    {formatCents(
                      sorted.reduce((s, e) => s + e.amountCents, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500">
                    {formatCents(
                      sorted.reduce((s, e) => s + e.taxAmountCents, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                    {formatCents(
                      sorted.reduce((s, e) => s + e.totalCents, 0)
                    )}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Expense Modal */}
      <PremiumModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={editingExpense ? "Edit Expense" : "Add Expense"}
        subtitle={
          editingExpense
            ? "Update the expense details"
            : "Record a new expense"
        }
        icon={Receipt}
        size="md"
        footer={
          <div className="flex items-center justify-between">
            <div>
              {editingExpense && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setModalOpen(false);
                  resetForm();
                }}
                disabled={submitting}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={submitting}
                onMouseEnter={() => setHoverSave(true)}
                onMouseLeave={() => setHoverSave(false)}
                style={{
                  backgroundColor: hoverSave
                    ? "var(--color-primary-hover)"
                    : "var(--color-primary)",
                }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          {/* OCR success banner */}
          {ocrBanner && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
              <Sparkles className="h-4 w-4 flex-shrink-0" />
              Receipt scanned — review extracted data below
            </div>
          )}

          {/* Error */}
          {formError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {formError}
            </div>
          )}

          {/* Date */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={form.date}
              onChange={(e) =>
                setForm((f) => ({ ...f, date: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="e.g. Office supplies, Court filing fee"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Vendor Name */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Vendor Name
            </label>
            <input
              type="text"
              value={form.vendorName}
              onChange={(e) =>
                setForm((f) => ({ ...f, vendorName: e.target.value }))
              }
              placeholder="e.g. Staples, FedEx"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Expense Account */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Expense Account <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={expenseAccountOptions}
              value={form.accountId || null}
              onChange={(val) =>
                setForm((f) => ({ ...f, accountId: val || "" }))
              }
              placeholder="Select expense account"
            />
          </div>

          {/* Amount + Tax row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Amount ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => {
                  const newAmount = e.target.value;
                  setForm((f) => ({
                    ...f,
                    amount: newAmount,
                    taxAmount: autoCalcTax(newAmount),
                  }));
                }}
                placeholder="0.00"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-mono focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                {getTaxLabel(taxConfig)} ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.taxAmount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, taxAmount: e.target.value }))
                }
                placeholder="0.00"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-mono focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>

          {/* Total (read-only) */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Total</label>
            <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-mono font-semibold text-slate-800">
              {formatCents(
                Math.round(
                  parseFloat(computeTotal(form.amount, form.taxAmount)) * 100
                ) || 0
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={PAYMENT_METHODS}
              value={form.paymentMethod || null}
              onChange={(val) =>
                setForm((f) => ({ ...f, paymentMethod: val || "" }))
              }
              placeholder="Select payment method"
            />
          </div>

          {/* Bank Account */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Bank Account
            </label>
            <SearchableSelect
              options={bankAccountOptions}
              value={form.bankAccountId || null}
              onChange={(val) =>
                setForm((f) => ({ ...f, bankAccountId: val || "" }))
              }
              placeholder="Select bank account (optional)"
              clearable
            />
          </div>

          {/* Matter */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Matter
            </label>
            <SearchableSelect
              options={matterOptions}
              value={form.matterId || null}
              onChange={(val) =>
                setForm((f) => ({ ...f, matterId: val || "" }))
              }
              placeholder="Link to a matter (optional)"
              clearable
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              rows={3}
              placeholder="Additional notes..."
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
            />
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default ExpensesTab;
